/**
 * The provider carries the TOGGLE that follows the bootstrap — it never
 * performs the first stamp. `data-theme` is already on `<html>` before this
 * module is parsed, written by the blocking inline script in `index.html`;
 * setting it from an effect here would paint the default theme first and
 * flash, which is the whole defect the bootstrap exists to prevent.
 *
 * The choice reaches the public site and the operator console through the
 * parent-domain COOKIE in `./cookie.ts`, not through the storage key below —
 * `localStorage` is partitioned by origin and the three surfaces are three
 * origins, so the shared key never crossed anything. Storage stays as the
 * per-origin fallback for a browser that refuses the cookie.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { serialiseThemeCookie } from './cookie';

export type Theme = 'dark' | 'light';

export const THEME_STORAGE_KEY = 'shahedni-theme';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStampedTheme(): Theme {
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStampedTheme);

  const setTheme = useCallback((next: Theme) => {
    document.documentElement.setAttribute('data-theme', next);
    try {
      document.cookie = serialiseThemeCookie(
        next,
        window.location.hostname,
        window.location.protocol === 'https:',
      );
    } catch {
      /* a refused cookie leaves the choice local to this origin */
    }
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* storage unavailable — the choice holds for this tab only */
    }
    setThemeState(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme,
      toggleTheme: () => {
        setTheme(theme === 'dark' ? 'light' : 'dark');
      },
    }),
    [theme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside <ThemeProvider>.');
  return context;
}
